'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoicesOrdersFilters } from '../../hooks/useInvoicesOrdersFilters';
import { useFilterOptionsForPanel } from '../../hooks/useFilterOptionsForPanel';
import { usePreInvoicesAndInvoicesLoadOptions } from '../../hooks/usePreInvoicesAndInvoicesLoadOptions';

interface InvoicesOrdersFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Órdenes de facturación (envíos elegibles).
 * Estado financiero, tipo, fecha creación y fecha envío.
 */
export const InvoicesOrdersFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: InvoicesOrdersFiltersPanelProps) => {
  const { t } = useTranslation('invoicesOrdersModule');

  const {
    filters,
    handleFinancialStatusChange,
    handleTypeChange,
    handleCreatedDateRangeChange,
    handleShippingDateRangeChange,
    handleDateTrackingUpdateDateRangeChange,
    handleLastStatusAtDateRangeChange,
    handleStatusIdsChange,
    handleShipmentStatusIdsChange,
    handleTeamIdsChange,
    handleShopIdsChange,
    handlePeriodChange,
    handlePaymentMethodIdsChange,
    handleCourierIdsChange,
    handlePreInvoiceIdsChange,
    handleInvoiceIdsChange,
    clearAllFilters,
  } = useInvoicesOrdersFilters({ initialConditions, onChange });

  const { loadPreInvoicesOptions, loadInvoicesOptions } = usePreInvoicesAndInvoicesLoadOptions();

  const { orderStatusOptions, shipmentStatusOptions, shopOptions, paymentMethodOptions, courierOptions, teamOptions } =
    useFilterOptionsForPanel();

  const periodOptions = useMemo(
    () => [
      { label: t('filters.periodOptions.monthly', 'Mensual'), value: 'MONTHLY' },
      { label: t('filters.periodOptions.weekly', 'Semanal'), value: 'WEEKLY' },
    ],
    [t],
  );

  const financialStatusOptions = useMemo(
    () => [
      { label: t('filters.financialStatusOptions.notInvoiced'), value: 'NOT_INVOICED' },
      { label: t('filters.financialStatusOptions.preinvoiced'), value: 'PREINVOICED' },
      { label: t('filters.financialStatusOptions.invoiced'), value: 'INVOICED' },
      { label: t('filters.financialStatusOptions.blocked'), value: 'BLOCKED' },
    ],
    [t],
  );

  const typeOptions = useMemo(
    () => [
      { label: t('filters.typeOptions.shipping'), value: 'SHIPPING' },
      { label: t('filters.typeOptions.return'), value: 'RETURN' },
      { label: t('filters.typeOptions.reshipment'), value: 'RESHIPMENT' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.financialStatus.length > 0) count++;
    if (filters.type.length > 0) count++;
    if (filters.createdDateRange.from && filters.createdDateRange.to) count++;
    if (filters.shippingDateRange.from && filters.shippingDateRange.to) count++;
    if (filters.dateTrackingUpdateDateRange.from && filters.dateTrackingUpdateDateRange.to) count++;
    if (filters.lastStatusAtDateRange.from && filters.lastStatusAtDateRange.to) count++;
    if (filters.statusIds.length > 0) count++;
    if (filters.shipmentStatusIds.length > 0) count++;
    if (filters.teamIds.length > 0) count++;
    if (filters.shopIds.length > 0) count++;
    if (filters.period.length > 0) count++;
    if (filters.paymentMethodIds.length > 0) count++;
    if (filters.courierIds.length > 0) count++;
    if (filters.preInvoiceIds.length > 0) count++;
    if (filters.invoiceIds.length > 0) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filters.financialStatus', 'Estado financiero')}>
          <MultiSelect
            options={financialStatusOptions}
            value={filters.financialStatus}
            onValueChange={handleFinancialStatusChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-financial-status"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.type', 'Tipo')}>
          <MultiSelect
            options={typeOptions}
            value={filters.type}
            onValueChange={handleTypeChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-type"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.createdAt', 'Fecha de creación')}>
          <DateRangePicker
            initialDateFrom={filters.createdDateRange.from}
            initialDateTo={filters.createdDateRange.to}
            onUpdate={({ range }) => handleCreatedDateRangeChange(range)}
            placeholder={t('filters.createdAt', 'Fecha de creación')}
            dataTestId="filter-created-at"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('filters.shippingDate', 'Fecha de envío')}>
          <DateRangePicker
            initialDateFrom={filters.shippingDateRange.from}
            initialDateTo={filters.shippingDateRange.to}
            onUpdate={({ range }) => handleShippingDateRangeChange(range)}
            placeholder={t('filters.shippingDate', 'Fecha de envío')}
            dataTestId="filter-shipping-date"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('filters.lastLogisticUpdate', 'Última actualización logística')}>
          <DateRangePicker
            initialDateFrom={filters.dateTrackingUpdateDateRange.from}
            initialDateTo={filters.dateTrackingUpdateDateRange.to}
            onUpdate={({ range }) => handleDateTrackingUpdateDateRangeChange(range)}
            placeholder={t('filters.lastLogisticUpdate', 'Última actualización logística')}
            dataTestId="filter-date-tracking-update"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('filters.lastStatusAt', 'Última fecha de estado')}>
          <DateRangePicker
            initialDateFrom={filters.lastStatusAtDateRange.from}
            initialDateTo={filters.lastStatusAtDateRange.to}
            onUpdate={({ range }) => handleLastStatusAtDateRangeChange(range)}
            placeholder={t('filters.lastStatusAt', 'Última fecha de estado')}
            dataTestId="filter-last-status-at"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('filters.orderStatus', 'Estado del pedido')}>
          <MultiSelect
            options={orderStatusOptions}
            value={filters.statusIds}
            onValueChange={handleStatusIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-order-status"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.shipmentStatus', 'Estado de envío')}>
          <MultiSelect
            options={shipmentStatusOptions}
            value={filters.shipmentStatusIds}
            onValueChange={handleShipmentStatusIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-shipment-status"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.client', 'Cliente')}>
          <MultiSelect
            options={teamOptions}
            value={filters.teamIds}
            onValueChange={handleTeamIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-team"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.store', 'Tienda')}>
          <MultiSelect
            options={shopOptions}
            value={filters.shopIds}
            onValueChange={handleShopIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-shop"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.period', 'Período')}>
          <MultiSelect
            options={periodOptions}
            value={filters.period}
            onValueChange={handlePeriodChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-period"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.paymentMethod', 'Método de pago')}>
          <MultiSelect
            options={paymentMethodOptions}
            value={filters.paymentMethodIds}
            onValueChange={handlePaymentMethodIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-payment-method"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.courier', 'Courier')}>
          <MultiSelect
            options={courierOptions}
            value={filters.courierIds}
            onValueChange={handleCourierIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            dataTestId="filter-courier"
            triggerClassName="h-[38px]"
          />
        </FilterField>

        <FilterField label={t('filters.preInvoice', 'Prefactura')}>
          <MultiSelect
            loadOptions={loadPreInvoicesOptions}
            value={filters.preInvoiceIds}
            onValueChange={handlePreInvoiceIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            searchPlaceholder="Buscar prefactura..."
            dataTestId="filter-pre-invoice"
            triggerClassName="h-[38px]"
            pageSize={20}
          />
        </FilterField>

        <FilterField label={t('filters.invoice', 'Factura')}>
          <MultiSelect
            loadOptions={loadInvoicesOptions}
            value={filters.invoiceIds}
            onValueChange={handleInvoiceIdsChange}
            placeholder="Seleccionar..."
            emptyText="Sin resultados"
            searchPlaceholder="Buscar factura..."
            dataTestId="filter-invoice"
            triggerClassName="h-[38px]"
            pageSize={20}
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="invoices-orders-clear-filters"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
